import { Fragment, useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { Lock } from 'lucide-react';
import { api, describeError, problemErrorCode } from '@/lib/api';
import { useAuthStore, useCanDoWriteAction } from '@/stores/auth';
import { Badge } from '@/components/ui';
import { orderBadge } from '@/components/badge-labels';
import { CardTable, THead, TBody, Tr, Th, Td } from '@/components/CardTable';

interface OrderItem {
  id: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  itemTotal: number;
}

interface StatusHistoryEntry {
  id: string;
  status: string;
  timestamp: string;
}

/** Recusa da API ao apagar nota de outro operador sem ser Admin. */
const NOTE_NOT_AUTHOR = 'platform.note.not_author';

/**
 * Quem escreveu a nota (tarefa 1.8 de platform-mcp-writes). Na nota de agente,
 * `authorAccountId` vem nulo: ela nunca aparece como escrita pelo Admin que
 * aprovou a delegação. A API anterior não manda o campo, e a nota é de operador.
 */
type SupportNoteAuthorKind = 'Operator' | 'Agent';

interface SupportNote {
  id: string;
  authorAccountId: string | null;
  authorKind?: SupportNoteAuthorKind;
  body: string;
  createdAt: string;
}

/** Promoção ou cupom gravado no pedido, na ordem das camadas: itens, pedido, entrega. */
interface AppliedPromotion {
  promotionId: string;
  kind: 'automatic' | 'coupon';
  code: string | null;
  name: string;
  target: 'items' | 'order' | 'delivery';
  amount: number;
}

interface OrderDetail {
  id: string;
  tenantId: string;
  trackingCode: string;
  status: string;
  deliveryMethod: string;
  customerName: string;
  customerPhone: string;
  customerId: string | null;
  paymentMethod: string;
  /** Soma bruta dos itens. */
  subtotal: number;
  /** Taxa bruta do modelo de entrega. */
  deliveryFee: number;
  /** Líquido de descontos: o que o cliente pagou. */
  total: number;
  createdAt: string;
  completedAt: string | null;
  operationallyPurgedAt: string | null;
  items: OrderItem[];
  statusHistory: StatusHistoryEntry[];
  supportNotes: SupportNote[];
  /** Pedidos anteriores às promoções vêm zerados e sem linhas. */
  discountTotal?: number;
  couponCode?: string | null;
  appliedPromotions?: AppliedPromotion[];
}

/** Linhas de desconto de uma camada; o suporte vê por que o total é menor que a soma. */
function discountRows(order: OrderDetail, delivery: boolean) {
  return (order.appliedPromotions ?? []).filter(
    (promotion) => (promotion.target === 'delivery') === delivery && promotion.amount > 0,
  );
}

function discountName(promotion: AppliedPromotion): string {
  return promotion.kind === 'coupon' && promotion.code ? `Cupom ${promotion.code}` : promotion.name;
}

/** Origem da navegação, preenchida por quem abriu o pedido. */
export interface OrderDetailNavState {
  /** URL completa (com filtros) da busca que levou até aqui. */
  from?: string;
}

export function OrderDetailPage() {
  const { tenantId, orderId } = useParams();
  // Voltar tem que devolver o operador para onde ele estava — a busca, com os
  // filtros que ele montou. Sem isso, quem chega pela tela de pedidos cai na
  // ficha do tenant e perde a busca inteira. Quando não há origem (URL colada,
  // favorito), a ficha do tenant é o destino sensato.
  const location = useLocation();
  const backTo = (location.state as OrderDetailNavState | null)?.from;
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Apagar nota é do autor ou do Admin; a API decide pelo papel Admin do token
  // e devolve 403 platform.note.not_author aos demais (C1). O botão só segue.
  const operatorId = useAuthStore((s) => s.operator?.id ?? null);
  const isAdmin = useAuthStore((s) => s.operator?.role) === 'Admin';
  // GUID compara sem caixa: o `sub` do token e o id da nota podem vir em caixas diferentes.
  const isAuthor = (authorId: string | null) =>
    operatorId !== null && authorId !== null && authorId.toLowerCase() === operatorId.toLowerCase();
  // Criar nota exige platform.order_notes.write na API (2.7), que todo papel
  // recebe; diante do token da API anterior, o seletor cai para o papel (nota
  // para todo operador, como antes).
  const canAddNote = useCanDoWriteAction('orderNotes');
  const [newNote, setNewNote] = useState('');
  const [noteError, setNoteError] = useState<string | null>(null);
  const [savingNote, setSavingNote] = useState(false);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get<OrderDetail>(`/platform/tenants/${tenantId}/orders/${orderId}`);
      setOrder(data);
      setError(null);
    } catch (err) {
      setError(describeError(err));
    }
  }, [tenantId, orderId]);

  /*
   * Falso positivo da regra, nao divida: todo `setState` deste callback roda
   * DEPOIS do `await`, que e o que a propria documentacao aponta como valido.
   * O bug esta registrado em facebook/react#34905, ainda aberto.
   *
   * Revisar quando o plugin subir de versao: se a supressao virar "unused
   * eslint-disable", e sinal de que corrigiram e a linha pode sair.
   */
  useEffect(() => {
    if (!tenantId || !orderId) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- ver nota acima
    void reload();
  }, [tenantId, orderId, reload]);

  async function addNote() {
    setNoteError(null);
    setSavingNote(true);
    try {
      await api.post(`/platform/tenants/${tenantId}/orders/${orderId}/notes`, { body: newNote });
      setNewNote('');
      await reload();
    } catch (err) {
      setNoteError(describeError(err));
    } finally {
      setSavingNote(false);
    }
  }

  async function deleteNote(id: string) {
    if (!confirm('Excluir esta nota?')) return;
    try {
      await api.delete(`/platform/tenants/${tenantId}/orders/${orderId}/notes/${id}`);
      await reload();
    } catch (err) {
      setNoteError(
        problemErrorCode(err) === NOTE_NOT_AUTHOR
          ? 'Só quem escreveu a nota ou um Admin pode removê-la.'
          : describeError(err),
      );
    }
  }

  if (error) return <div className="error-banner">{error}</div>;
  if (!order) return <p className="muted">Carregando…</p>;

  const status = orderBadge(order.status);

  return (
    <div className="page page-narrow" style={{ gap: 18 }}>
      {/* aria-label explicito: o menu lateral tambem tem um link "Pedidos", e
          dois links com o mesmo nome acessivel na pagina sao ambiguos para quem
          navega por leitor de tela. */}
      <Link
        to={backTo ?? `/tenants/${tenantId}`}
        className="backlink"
        aria-label={backTo ? 'Voltar para a busca de pedidos' : 'Voltar para a loja'}
      >
        ← {backTo ? 'Pedidos' : 'Loja'}
      </Link>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2
          style={{
            fontFamily: 'var(--font-heading)',
            fontSize: 24,
            fontWeight: 800,
            margin: 0,
            letterSpacing: '-0.02em',
          }}
        >
          Pedido <span className="mono" style={{ fontSize: 20 }}>{order.trackingCode}</span>
        </h2>
        <Badge tone={status.tone} dot>{status.label}</Badge>
        {/* A API serializa o enum como "Delivery"/"Pickup"; comparar com
            'delivery' minúsculo rotulava toda entrega como retirada. */}
        <Badge tone="info">
          {order.deliveryMethod.toLowerCase() === 'delivery' ? 'Entrega' : 'Retirada'}
        </Badge>
        {order.operationallyPurgedAt && <Badge tone="warning">Purgado</Badge>}
      </div>

      <div className="grid-halves">
        <div className="card">
          <div className="card-title" style={{ marginBottom: 14 }}>Cliente</div>
          <div className="detail-grid">
            <div className="k">Nome</div><div>{order.customerName}</div>
            <div className="k">Telefone</div><div>{order.customerPhone}</div>
            <div className="k">Pagamento</div><div>{order.paymentMethod}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-title" style={{ marginBottom: 14 }}>Totais</div>
          <div className="row-label-value" data-order-totals>
            <div className="muted">Subtotal</div>
            <div style={{ textAlign: 'right' }}>R$ {order.subtotal.toFixed(2)}</div>
            {discountRows(order, false).map((promotion) => (
              <Fragment key={`${promotion.promotionId}:${promotion.target}`}>
                <div className="muted">{discountName(promotion)}</div>
                <div style={{ textAlign: 'right' }}>− R$ {promotion.amount.toFixed(2)}</div>
              </Fragment>
            ))}
            <div className="muted">Entrega</div>
            <div style={{ textAlign: 'right' }}>R$ {order.deliveryFee.toFixed(2)}</div>
            {discountRows(order, true).map((promotion) => (
              <Fragment key={`${promotion.promotionId}:${promotion.target}`}>
                <div className="muted">{discountName(promotion)}</div>
                <div style={{ textAlign: 'right' }}>− R$ {promotion.amount.toFixed(2)}</div>
              </Fragment>
            ))}
            <div style={{ fontWeight: 700, borderTop: '1px solid var(--color-border)', paddingTop: 10 }}>
              Total
            </div>
            <div
              style={{
                textAlign: 'right',
                fontWeight: 700,
                fontSize: 17,
                color: 'var(--color-primary-text)',
                borderTop: '1px solid var(--color-border)',
                paddingTop: 10,
              }}
            >
              R$ {order.total.toFixed(2)}
            </div>
          </div>
        </div>
      </div>

      <div className="grid-main-aside">
        <div className="card card-flush">
          <div className="card-head">
            <div className="card-title">Itens</div>
          </div>
          <div className="table-scroll">
            <CardTable>
              <THead>
                <Tr>
                  <Th>Produto</Th>
                  <Th style={{ textAlign: 'center' }}>Qtd</Th>
                  <Th style={{ textAlign: 'right' }}>Unit.</Th>
                  <Th style={{ textAlign: 'right' }}>Total</Th>
                </Tr>
              </THead>
              <TBody>
                {order.items.map((i) => (
                  <Tr key={i.id}>
                    <Td heading>{i.productName}</Td>
                    <Td label="Qtd" style={{ textAlign: 'center' }} className="muted">{i.quantity}</Td>
                    <Td label="Unit." style={{ textAlign: 'right' }} className="muted">R$ {i.unitPrice.toFixed(2)}</Td>
                    <Td label="Total" style={{ textAlign: 'right', fontWeight: 600 }}>R$ {i.itemTotal.toFixed(2)}</Td>
                  </Tr>
                ))}
              </TBody>
            </CardTable>
          </div>
        </div>

        <div className="card">
          <div className="card-title" style={{ marginBottom: 16 }}>Histórico de status</div>
          {order.statusHistory.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>Sem transições registradas.</p>
          ) : (
            <div className="timeline">
              {order.statusHistory.map((h) => (
                <div key={h.id} className="timeline-item">
                  <strong>{orderBadge(h.status).label}</strong>
                  <span>{new Date(h.timestamp).toLocaleString('pt-BR')}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <Lock size={16} />
          <div className="card-title">Notas internas</div>
        </div>
        <div className="card-subtitle" style={{ marginBottom: 14 }}>
          Visíveis apenas para operadores da plataforma.
        </div>

        {noteError && <div className="error-banner" style={{ marginBottom: 12 }}>{noteError}</div>}

        {canAddNote && (
          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Anotação visível apenas para operadores plataforma…"
              aria-label="Nova nota interna"
              rows={2}
              style={{
                flex: 1,
                padding: '10px 12px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--color-border-strong)',
                fontFamily: 'inherit',
                fontSize: 14,
                resize: 'vertical',
              }}
            />
            <button
              className="btn"
              onClick={addNote}
              disabled={savingNote || !newNote.trim()}
            >
              {savingNote ? 'Salvando…' : 'Adicionar'}
            </button>
          </div>
        )}

        {order.supportNotes.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>Ainda sem notas neste pedido.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {order.supportNotes.map((n) => (
              <div key={n.id} className="note-bubble">
                <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.55 }}>{n.body}</div>
                <footer>
                  {n.authorKind === 'Agent' || n.authorAccountId === null ? (
                    <span>
                      <Badge tone="warning" title="Escrita por um agente com delegação, sem autor humano">
                        Nota da IA
                      </Badge>
                    </span>
                  ) : (
                    <span>por {n.authorAccountId.slice(0, 8)}…</span>
                  )}
                  <span>
                    {new Date(n.createdAt).toLocaleString('pt-BR')}
                    {(isAdmin || isAuthor(n.authorAccountId)) && (
                      <>
                        {' · '}
                        <a onClick={() => deleteNote(n.id)} style={{ fontWeight: 600 }}>Remover</a>
                      </>
                    )}
                  </span>
                </footer>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
